/**
 * ZIP rate-card CSV importer.
 *
 * Absorbs operator rate cards in a canonical CSV format and turns them into ZIP
 * zones + per-vehicle-class flat rate rules. Rules always apply between any
 * configured hub and the ZIP zone (either direction) — there are no match modes.
 *
 * Expected columns (header names are case/space/underscore-insensitive):
 *   zip_code   (required) — single, slash/comma list, or range (27601-27617)
 *   zone_name  (optional) — groups rows into one zone; if absent, rows that
 *                           share an identical price tuple are auto-grouped
 *   <any other column>    — a vehicle CLASS (e.g. sedan, suv). The header is
 *                           matched to vehicles.vehicle_type; an empty cell
 *                           means "no rate for that class".
 *
 * On import: one zip_flat_rate_zones row per zone, then one zip_flat_rate_rules
 * row per (zone × class) priced for that class's matching vehicles.
 */

import { useCallback, useMemo, useRef, useState } from 'react'
import Papa from 'papaparse'
import { useQueryClient } from '@tanstack/react-query'
import { supabase } from '@/lib/supabase'
import type { Tables, TablesInsert } from '@limo/database'
import { expandZipSpec } from '@limo/shared/pricing'

import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog'
import { Button } from '@/components/ui/button'
import { ScrollArea } from '@/components/ui/scroll-area'
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table'
import { Upload, FileText, AlertCircle, Loader2, RefreshCw, Download } from 'lucide-react'
import { useToast } from '@/hooks/use-toast'

const ZONE_COLORS = ['#3B82F6', '#10B981', '#F59E0B', '#EF4444', '#8B5CF6', '#EC4899', '#06B6D4', '#F97316']

const normalizeHeader = (h: string): string =>
  h.toLowerCase().trim().replace(/[^a-z0-9]+/g, '_').replace(/^_+|_+$/g, '')

const ZIP_HEADERS = new Set(['zip_code', 'zip', 'zipcode', 'zip_codes', 'zips'])
const ZONE_HEADERS = new Set(['zone_name', 'zone', 'name', 'city', 'city_name'])

type Vehicle = Tables<'vehicles'>

/** A class column detected in the CSV, mapped to platform vehicles by vehicle_type. */
interface ClassColumn {
  readonly header: string // original header text
  readonly vehicleIds: string[] // vehicles whose vehicle_type matches
}

/** A grouped zone built from one or more CSV rows. */
interface PreviewZone {
  readonly name: string
  readonly zipCodes: string[]
  readonly invalidTokens: number
  readonly prices: Record<string, number> // class header -> price
}

interface Props {
  readonly open: boolean
  readonly onOpenChange: (open: boolean) => void
  readonly accountId: string
  readonly vehicles: Vehicle[]
}

const TEMPLATE_CSV =
  'zip_code,zone_name,sedan,suv\n' +
  '30002,Avondale Estates,114.50,197.00\n' +
  '28001/28002,Albemarle,175.00,219.00\n' +
  '27601-27617,Raleigh,150.00,250.00\n'

export function ZipRateImportDialog({ open, onOpenChange, accountId, vehicles }: Props) {
  const { toast } = useToast()
  const queryClient = useQueryClient()
  const inputRef = useRef<HTMLInputElement>(null)

  const [fileName, setFileName] = useState<string | null>(null)
  const [parseError, setParseError] = useState<string | null>(null)
  const [zones, setZones] = useState<PreviewZone[]>([])
  const [classColumns, setClassColumns] = useState<ClassColumn[]>([])
  const [isDragging, setIsDragging] = useState(false)
  const [importing, setImporting] = useState(false)

  const reset = useCallback(() => {
    setFileName(null)
    setParseError(null)
    setZones([])
    setClassColumns([])
    setIsDragging(false)
    setImporting(false)
  }, [])

  const handleOpenChange = useCallback(
    (next: boolean) => {
      if (!next) reset()
      onOpenChange(next)
    },
    [onOpenChange, reset]
  )

  const parseText = useCallback(
    (text: string) => {
      const result = Papa.parse<Record<string, string>>(text, {
        header: true,
        skipEmptyLines: true,
      })
      if (result.errors && result.errors.length > 0) {
        setParseError(result.errors[0]?.message ?? 'Could not parse the CSV.')
        setZones([])
        setClassColumns([])
        return
      }

      const headers = result.meta.fields ?? []
      let zipHeader: string | undefined
      let zoneHeader: string | undefined
      const classHeaders: string[] = []
      for (const h of headers) {
        const norm = normalizeHeader(h)
        if (!zipHeader && ZIP_HEADERS.has(norm)) zipHeader = h
        else if (!zoneHeader && ZONE_HEADERS.has(norm)) zoneHeader = h
        else classHeaders.push(h)
      }

      if (!zipHeader) {
        setParseError('No "zip_code" column found in the file.')
        setZones([])
        setClassColumns([])
        return
      }

      // Map each class column to vehicles by matching normalized header to vehicle_type.
      const cols: ClassColumn[] = classHeaders.map((header) => {
        const normClass = normalizeHeader(header)
        const vehicleIds = vehicles
          .filter((v) => normalizeHeader(v.vehicle_type || '') === normClass)
          .map((v) => v.id)
        return { header, vehicleIds }
      })

      const parsePrice = (raw: string | undefined): number => {
        if (!raw) return 0
        const n = parseFloat(String(raw).replace(/[^0-9.]/g, ''))
        return Number.isFinite(n) ? n : 0
      }

      // Build grouped zones. Group by zone name when present, else by price tuple.
      const groups = new Map<string, PreviewZone>()
      let autoIndex = 0
      const autoKeyToName = new Map<string, string>()

      for (const row of result.data) {
        const rawZip = row[zipHeader] ?? ''
        const tokens = String(rawZip).split(/[/,;&]|\s+/).filter((t) => t.trim().length > 0)
        const zips = expandZipSpec(rawZip)
        const invalid = Math.max(0, tokens.length - zips.length)
        if (zips.length === 0) continue

        const prices: Record<string, number> = {}
        for (const h of classHeaders) prices[h] = parsePrice(row[h])

        const explicitName = zoneHeader ? String(row[zoneHeader] ?? '').trim() : ''
        let groupKey: string
        let zoneName: string
        if (explicitName) {
          groupKey = `name:${explicitName.toLowerCase()}`
          zoneName = explicitName
        } else {
          const tuple = classHeaders.map((h) => prices[h]).join('|')
          if (!autoKeyToName.has(tuple)) {
            autoIndex += 1
            autoKeyToName.set(tuple, `Imported Zone ${autoIndex}`)
          }
          groupKey = `tuple:${tuple}`
          zoneName = autoKeyToName.get(tuple)!
        }

        const existing = groups.get(groupKey)
        if (existing) {
          const merged = new Set([...existing.zipCodes, ...zips])
          groups.set(groupKey, {
            ...existing,
            zipCodes: [...merged],
            invalidTokens: existing.invalidTokens + invalid,
            prices: classHeaders.reduce((acc, h) => {
              acc[h] = existing.prices[h] || prices[h]
              return acc
            }, {} as Record<string, number>),
          })
        } else {
          groups.set(groupKey, { name: zoneName, zipCodes: zips, invalidTokens: invalid, prices })
        }
      }

      setClassColumns(cols)
      setZones([...groups.values()])
      setParseError(null)
    },
    [vehicles]
  )

  const onFiles = useCallback(
    (files: FileList | null) => {
      const file = files?.[0]
      if (!file) return
      setFileName(file.name)
      const reader = new FileReader()
      reader.onload = () => parseText(String(reader.result ?? ''))
      reader.onerror = () => setParseError('Could not read the file.')
      reader.readAsText(file)
    },
    [parseText]
  )

  const downloadTemplate = useCallback(() => {
    const blob = new Blob([TEMPLATE_CSV], { type: 'text/csv' })
    const url = URL.createObjectURL(blob)
    const a = document.createElement('a')
    a.href = url
    a.download = 'zip-rate-template.csv'
    a.click()
    URL.revokeObjectURL(url)
  }, [])

  const unmatchedClasses = useMemo(
    () => classColumns.filter((c) => c.vehicleIds.length === 0).map((c) => c.header),
    [classColumns]
  )

  const totalRules = useMemo(() => {
    let count = 0
    for (const zone of zones) {
      for (const col of classColumns) {
        if (col.vehicleIds.length > 0 && (zone.prices[col.header] || 0) > 0) count += 1
      }
    }
    return count
  }, [zones, classColumns])

  const canImport = zones.length > 0 && totalRules > 0 && !importing && parseError === null

  const handleImport = useCallback(async () => {
    if (!canImport) return
    setImporting(true)
    try {
      // 1. Insert zones, returning their ids.
      const zonePayload: TablesInsert<'zip_flat_rate_zones'>[] = zones.map((z, i) => ({
        account_id: accountId,
        name: z.name,
        zip_codes: z.zipCodes,
        color: ZONE_COLORS[i % ZONE_COLORS.length],
        is_active: true,
      }))

      const { data: insertedZones, error: zonesError } = await supabase
        .from('zip_flat_rate_zones')
        .insert(zonePayload)
        .select('id, name')
      if (zonesError) throw zonesError

      const nameToId = new Map<string, string>()
      for (const z of insertedZones ?? []) nameToId.set(z.name, z.id)

      // 2. One rule per (zone × class with a price and matching vehicles).
      const rulePayload: TablesInsert<'zip_flat_rate_rules'>[] = []
      for (const zone of zones) {
        const zoneId = nameToId.get(zone.name)
        if (!zoneId) continue
        for (const col of classColumns) {
          const price = zone.prices[col.header] || 0
          if (col.vehicleIds.length === 0 || price <= 0) continue
          rulePayload.push({
            account_id: accountId,
            name: `${zone.name} — ${col.header}`,
            zip_zone_id: zoneId,
            flat_rate: price,
            vehicle_ids: col.vehicleIds,
            priority: 0,
            is_active: true,
          })
        }
      }

      if (rulePayload.length > 0) {
        const { error: rulesError } = await supabase.from('zip_flat_rate_rules').insert(rulePayload)
        if (rulesError) throw rulesError
      }

      queryClient.invalidateQueries({ queryKey: ['zip-flat-rate-zones', accountId] })
      queryClient.invalidateQueries({ queryKey: ['zip-flat-rate-rules', accountId] })
      toast({
        title: 'Import complete',
        description: `${insertedZones?.length ?? 0} zone(s) and ${rulePayload.length} rule(s) created.`,
      })
      handleOpenChange(false)
    } catch (err) {
      toast({
        title: 'Import failed',
        description: err instanceof Error ? err.message : 'Unknown error',
        variant: 'destructive',
      })
    } finally {
      setImporting(false)
    }
  }, [canImport, zones, classColumns, accountId, queryClient, toast, handleOpenChange])

  const hasFile = fileName !== null && parseError === null && zones.length >= 0

  return (
    <Dialog open={open} onOpenChange={handleOpenChange}>
      <DialogContent className="sm:max-w-3xl max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>Import ZIP rate card</DialogTitle>
          <DialogDescription>
            Upload a CSV of ZIP codes and per-vehicle-class prices. Rules apply between any hub and
            the ZIP zone (either direction).
          </DialogDescription>
        </DialogHeader>

        <div className="flex justify-end">
          <Button variant="outline" size="sm" className="gap-1.5" onClick={downloadTemplate}>
            <Download className="h-3.5 w-3.5" />
            Download template
          </Button>
        </div>

        {!hasFile || parseError ? (
          <>
            <label
              onDragOver={(e) => {
                e.preventDefault()
                setIsDragging(true)
              }}
              onDragLeave={() => setIsDragging(false)}
              onDrop={(e) => {
                e.preventDefault()
                setIsDragging(false)
                onFiles(e.dataTransfer.files)
              }}
              className={`flex cursor-pointer flex-col items-center justify-center gap-2 rounded-lg border-2 border-dashed p-8 text-center transition-colors ${
                isDragging ? 'border-primary bg-primary/5' : 'border-muted-foreground/25 hover:border-primary/50'
              }`}
            >
              <Upload className="h-8 w-8 text-muted-foreground" />
              <p className="text-sm font-medium">Drop a CSV here or click to choose</p>
              <p className="max-w-md text-xs text-muted-foreground">
                Columns: <code>zip_code</code>, <code>zone_name</code> (optional), and one column per
                vehicle class (e.g. <code>sedan</code>, <code>suv</code>).
              </p>
              <input
                ref={inputRef}
                type="file"
                accept=".csv,text/csv"
                className="hidden"
                onChange={(e) => {
                  onFiles(e.target.files)
                  e.target.value = ''
                }}
              />
            </label>
            {parseError ? (
              <p className="flex items-center gap-2 text-sm text-destructive">
                <AlertCircle className="h-4 w-4" />
                {parseError}
              </p>
            ) : null}
          </>
        ) : (
          <div className="space-y-4">
            {/* File + change */}
            <div className="flex items-center justify-between gap-2 rounded-md border bg-muted/30 px-3 py-2">
              <div className="flex min-w-0 items-center gap-2">
                <FileText className="h-4 w-4 flex-shrink-0 text-muted-foreground" />
                <span className="truncate text-sm font-medium">{fileName}</span>
                <span className="text-xs text-muted-foreground">({zones.length} zones)</span>
              </div>
              <Button variant="ghost" size="sm" className="gap-1.5" onClick={() => inputRef.current?.click()}>
                <RefreshCw className="h-3.5 w-3.5" />
                Choose different file
              </Button>
              <input
                ref={inputRef}
                type="file"
                accept=".csv,text/csv"
                className="hidden"
                onChange={(e) => {
                  onFiles(e.target.files)
                  e.target.value = ''
                }}
              />
            </div>

            {/* Detected classes */}
            <div className="rounded-md border p-3">
              <p className="mb-2 text-xs font-semibold uppercase tracking-wide text-muted-foreground">
                Vehicle classes detected
              </p>
              <div className="flex flex-wrap gap-2 text-sm">
                {classColumns.length === 0 ? (
                  <span className="text-muted-foreground">No price columns found</span>
                ) : (
                  classColumns.map((c) => (
                    <span
                      key={c.header}
                      className={`rounded px-2 py-0.5 text-xs ${
                        c.vehicleIds.length > 0
                          ? 'bg-green-100 text-green-800'
                          : 'bg-muted text-muted-foreground'
                      }`}
                    >
                      {c.header} → {c.vehicleIds.length} vehicle{c.vehicleIds.length === 1 ? '' : 's'}
                    </span>
                  ))
                )}
              </div>
              {unmatchedClasses.length > 0 && (
                <p className="mt-2 flex items-center gap-1.5 text-xs text-amber-700">
                  <AlertCircle className="h-3.5 w-3.5" />
                  No vehicle matches vehicle_type for: {unmatchedClasses.join(', ')} — those columns
                  will be skipped.
                </p>
              )}
            </div>

            {/* Preview */}
            <ScrollArea className="h-[260px] rounded-md border">
              <Table>
                <TableHeader className="sticky top-0 bg-background">
                  <TableRow>
                    <TableHead>Zone</TableHead>
                    <TableHead className="text-center">ZIPs</TableHead>
                    {classColumns.map((c) => (
                      <TableHead key={c.header} className="text-right">
                        {c.header}
                      </TableHead>
                    ))}
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {zones.map((zone) => (
                    <TableRow key={zone.name}>
                      <TableCell className="font-medium">
                        {zone.name}
                        {zone.invalidTokens > 0 && (
                          <span className="ml-2 text-xs text-amber-700">
                            ({zone.invalidTokens} skipped)
                          </span>
                        )}
                      </TableCell>
                      <TableCell className="text-center tabular-nums text-sm">
                        {zone.zipCodes.length}
                      </TableCell>
                      {classColumns.map((c) => (
                        <TableCell key={c.header} className="text-right tabular-nums text-sm">
                          {zone.prices[c.header] > 0 ? `$${zone.prices[c.header].toFixed(2)}` : '—'}
                        </TableCell>
                      ))}
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </ScrollArea>

            <p className="text-sm text-muted-foreground">
              Will create <span className="font-medium text-foreground">{zones.length}</span> zone(s)
              and <span className="font-medium text-foreground">{totalRules}</span> rule(s).
            </p>
          </div>
        )}

        <DialogFooter>
          <Button variant="outline" onClick={() => handleOpenChange(false)} disabled={importing}>
            Cancel
          </Button>
          <Button onClick={handleImport} disabled={!canImport}>
            {importing ? (
              <>
                <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                Importing…
              </>
            ) : (
              `Import ${zones.length} zone${zones.length === 1 ? '' : 's'}`
            )}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  )
}
